import type { SpaceGits } from "@indietabletop/types";
import type {
  RepairProfile,
  Ruleset,
  TreatmentLocation,
  TreatmentProfile,
} from "./Ruleset.ts";

export class Treatment {
  readonly ruleset: Ruleset;
  readonly location: TreatmentLocation;
  private data: TreatmentProfile;

  constructor(location: TreatmentLocation, data: TreatmentProfile) {
    this.ruleset = location.ruleset;
    this.location = location;
    this.data = data;
  }

  private get resolvedData(): Extract<RepairProfile, { fromMod?: false }> {
    const data = this.data;

    if (!data.fromGear) {
      return data;
    }

    const gear = this.ruleset.gear.get(data.id).valueOrNull();
    if (!gear) {
      return {
        id: data.id,
        selectOn: data.selectOn,
        name: "Unknown",
        description: `Referenced gear "${data.id}" not found.`,
        effects: [],
      };
    }

    return {
      id: data.id,
      name: gear.name,
      description: data.description ?? gear.description,
      effects: gear.effects ?? [],
      selectOn: data.selectOn,
    };
  }

  get id() {
    return this.data.id;
  }

  get compositeId() {
    return Treatment.toCompositeId(this);
  }

  get name() {
    return this.resolvedData.name;
  }

  get selectOn() {
    return this.data.selectOn;
  }

  get effects() {
    return this.resolvedData.effects;
  }

  get description() {
    return this.resolvedData.description;
  }

  get fromGear() {
    return this.data.fromGear ?? false;
  }

  matchesRef(ref: SpaceGits.TreatmentRef) {
    return ref.id === this.id && ref.locationId === this.location.id;
  }

  toTreatmentRef(): SpaceGits.TreatmentRef {
    return { locationId: this.location.id, id: this.id };
  }

  static toCompositeId(treatment: Treatment) {
    return `${treatment.location.id}/${treatment.id}`;
  }

  static splitCompositeId(compositeId: string): SpaceGits.TreatmentRef {
    const [locationId, id] = compositeId.split("/") as [string, string];
    return { locationId, id };
  }
}
